import { byteLength, ConflictError, ExistsError, NotFoundError, newVersion } from "./errors.ts";
import type { DocMeta, ReadResult, Store, WriteOptions, WriteResult } from "./types.ts";

const STORE = "docs";
const ASSETS = "assets";

/**
 * The library kept in the browser's own storage. This is the store Firefox
 * uses, and the one Chromium uses until a folder is linked.
 */
interface Record_ extends ReadResult {
  path: string;
  size: number;
}

export class IdbStore implements Store {
  kind = "idb";
  label = "Browser library";

  #db: Promise<IDBDatabase> | null = null;
  #name: string;
  #idb: IDBFactory;

  // The default name is kept from the working name so existing libraries stay readable.
  constructor({
    name = "looseleaf",
    idb = globalThis.indexedDB,
  }: { name?: string; idb?: IDBFactory } = {}) {
    this.#name = name;
    this.#idb = idb;
  }

  #open(): Promise<IDBDatabase> {
    this.#db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = this.#idb.open(this.#name, 2);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "path" });
        if (!db.objectStoreNames.contains(ASSETS))
          db.createObjectStore(ASSETS, { keyPath: "path" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this.#db;
  }

  /** Runs one request inside its own transaction. */
  async #request<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.#open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async list(): Promise<DocMeta[]> {
    const records = await this.#request<Record_[]>("readonly", (s) => s.getAll());
    return records.map((r) => ({
      path: r.path,
      name: r.path,
      updatedAt: r.updatedAt,
      size: r.size,
    }));
  }

  async read(path: string): Promise<ReadResult> {
    const r = await this.#request<Record_ | undefined>("readonly", (s) => s.get(path));
    if (!r) throw new NotFoundError(path);
    return { text: r.text, version: r.version, updatedAt: r.updatedAt };
  }

  async readAll(): Promise<(ReadResult & { path: string })[]> {
    const records = await this.#request<Record_[]>("readonly", (s) => s.getAll());
    return records.map(({ path, text, version, updatedAt }) => ({
      path,
      text,
      version,
      updatedAt,
    }));
  }

  /**
   * The check and the write happen in one transaction, so two tabs cannot
   * both pass the check.
   */
  async write(path: string, text: string, options: WriteOptions = {}): Promise<WriteResult> {
    const db = await this.#open();
    return new Promise<WriteResult>((resolve, reject) => {
      let failure: Error | null = null;
      const record: Record_ = {
        path,
        text,
        version: newVersion(),
        updatedAt: Date.now(),
        size: byteLength(text),
      };
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const get = store.get(path);

      get.onsuccess = () => {
        const existing = get.result as Record_ | undefined;
        if (options.create && existing) failure = new ExistsError(path);
        else if (
          options.expectVersion !== undefined &&
          existing?.version !== options.expectVersion
        ) {
          failure = new ConflictError(path);
        }
        if (failure) {
          tx.abort();
          return;
        }
        store.put(record);
      };
      tx.oncomplete = () => resolve({ version: record.version, updatedAt: record.updatedAt });
      tx.onabort = () => reject(failure ?? tx.error);
      tx.onerror = () => reject(failure ?? tx.error);
    });
  }

  async remove(path: string): Promise<void> {
    await this.read(path);
    await this.#request("readwrite", (s) => s.delete(path));
  }

  /** Runs one request on the assets store. */
  async #assetRequest<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.#open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(ASSETS, mode);
      const request = run(tx.objectStore(ASSETS));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async writeAsset(path: string, data: Blob): Promise<void> {
    await this.#assetRequest("readwrite", (s) => s.put({ path, data }));
  }

  async readAsset(path: string): Promise<Blob | null> {
    const r = await this.#assetRequest<{ data: Blob } | undefined>("readonly", (s) => s.get(path));
    return r?.data ?? null;
  }

  async listAssets(): Promise<string[]> {
    return this.#assetRequest<string[]>("readonly", (s) => s.getAllKeys() as IDBRequest<string[]>);
  }

  async removeAsset(path: string): Promise<void> {
    await this.#assetRequest("readwrite", (s) => s.delete(path));
  }
}
